import { createContext, useContext } from "react";
import type { Product, Quote } from "@fa/shared";

export const ApiContext = createContext<{ baseUrl: string }>({ baseUrl: "/api" });

export function useApiBase(): string {
  return useContext(ApiContext).baseUrl;
}

export async function submit(
  baseUrl: string,
  product: Product,
  body: unknown,
): Promise<{ reference: string; quote: Quote }> {
  const res = await fetch(`${baseUrl}/submissions/${product}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function initiateStk(
  baseUrl: string,
  body: {
    reference: string;
    phoneNumber: string;
    amount: string;
    accountNumber: string;
  },
) {
  const res = await fetch(`${baseUrl}/payments/stk`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`STK request failed (${res.status}): ${await res.text()}`);
  return res.json();
}

export type StkStatus =
  | "PENDING"
  | "SUCCESS"
  | "FAILED"
  | "CANCELLED"
  | "TIMEOUT"
  | "UNKNOWN";

export async function getStkStatus(
  baseUrl: string,
  reference: string,
): Promise<{ status: StkStatus; receipt?: string }> {
  const res = await fetch(`${baseUrl}/payments/status/${reference}`);
  if (!res.ok) return { status: "UNKNOWN" };
  return res.json();
}

/**
 * Polls the status endpoint every `intervalMs` up to `timeoutMs`. Calls
 * `onTick` with each observed status so the UI can update. Resolves with
 * the terminal status (or the last observed one at timeout).
 */
export async function pollStkStatus(
  baseUrl: string,
  reference: string,
  opts: {
    intervalMs?: number;
    timeoutMs?: number;
    onTick?: (s: { status: StkStatus; receipt?: string }) => void;
    signal?: AbortSignal;
  } = {},
): Promise<{ status: StkStatus; receipt?: string }> {
  const interval = opts.intervalMs ?? 4000;
  const deadline = Date.now() + (opts.timeoutMs ?? 120_000);
  let last: { status: StkStatus; receipt?: string } = { status: "PENDING" };
  while (Date.now() < deadline) {
    if (opts.signal?.aborted) return last;
    last = await getStkStatus(baseUrl, reference);
    opts.onTick?.(last);
    if (
      last.status === "SUCCESS" ||
      last.status === "FAILED" ||
      last.status === "CANCELLED" ||
      last.status === "TIMEOUT"
    ) {
      return last;
    }
    await new Promise((r) => setTimeout(r, interval));
  }
  return last;
}

export async function uploadDocument(
  baseUrl: string,
  reference: string,
  file: File,
  kind: string,
): Promise<{ id: string; filename: string; bytes: number; kind: string }> {
  const form = new FormData();
  form.append("kind", kind);
  form.append("file", file, file.name);
  const res = await fetch(
    `${baseUrl}/submissions/${reference}/documents/upload`,
    { method: "POST", body: form },
  );
  if (!res.ok) throw new Error(`Upload failed (${res.status}): ${await res.text()}`);
  return res.json();
}

export async function submitDocs(baseUrl: string, reference: string) {
  const res = await fetch(
    `${baseUrl}/submissions/${reference}/documents/submit`,
    { method: "POST" },
  );
  if (!res.ok) throw new Error(`Finalize failed (${res.status}): ${await res.text()}`);
  return res.json() as Promise<{ ok: boolean; attached: number }>;
}

/* ------------------------------------------------------------------ */
/*  Member validation                                                  */
/* ------------------------------------------------------------------ */

export interface MemberValidationResult {
  valid: boolean;
  firstName?: string;
  lastName?: string;
  phone?: string;
  reason?: string;
}

export async function validateMember(
  baseUrl: string,
  memberNumber: string,
): Promise<MemberValidationResult> {
  const encoded = encodeURIComponent(memberNumber.trim());
  const res = await fetch(`${baseUrl}/members/validate/${encoded}`);
  if (!res.ok) {
    if (res.status === 429) throw new Error("Too many attempts. Please wait and try again.");
    throw new Error("Member validation service unavailable. Please try again later.");
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/*  Member documents (SharePoint)                                      */
/* ------------------------------------------------------------------ */

export interface MemberDocument {
  name: string;
  kind: string;
  uploadedAt: string;
}

export async function listMemberDocuments(
  baseUrl: string,
  identifier: string,
): Promise<MemberDocument[]> {
  const encoded = encodeURIComponent(identifier.trim());
  const res = await fetch(`${baseUrl}/members/${encoded}/documents`);
  if (!res.ok) return []; // Silently fail — don't block the flow
  const data = (await res.json()) as { documents?: MemberDocument[] };
  return data.documents ?? [];
}
